import * as React from "react";
import { InputProps } from "../../interfaces/props/InputProps";
import { TooltipHost, Stack, IStackTokens, TagPicker, ITag, IBasePickerSuggestionsProps, Label, Spinner, SpinnerSize, FontIcon } from "@fluentui/react";
import { DatasourceService } from "../../services/DatasourceService";
import { connect } from "react-redux";
import { RootState } from "../../redux";
import { DataSourcePanel } from "./datasourcePanel";
import { INameValuePairIndex } from "../../interfaces/INameValuePairIndex";
import { DatasourceHelper } from "../helpers/dataSourceHelper";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { logger } from "../../../../utilities/logger";
import { IDatasourceQueryDto } from "../../interfaces/IDatasourceQueryDto";
import { SharePointMetadataBooleans } from "../../config/sharepointConstants";
import { sortTerms } from "../helpers/tagHelper";

const listContainsTagList = (tag: ITag, tagList?: ITag[]) => {
    if (!tagList || !tagList.length || tagList.length === 0) {
        return false;
    }
    return tagList.some(compareTag => compareTag.key === tag.key);
};

const getTextFromItem = (item: ITag) => item.name;

const mapStateToProps = (state: RootState) => {
    return {
        datasourceItems: state.tenantInfo.datasources,
        currentDirectory: state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null
    };
};

interface DatasourceTagPickerProps extends InputProps, ReduxProps { }

type ReduxProps = ReturnType<typeof mapStateToProps>;

export const UnconnectedDatasourceTagPicker: React.FunctionComponent<DatasourceTagPickerProps> = ({
    displayName,
    isRequired,
    inputValue,
    inputValidator,
    updateInputValidationStatus,
    error,
    datasourceItems,
    currentDirectory
}: DatasourceTagPickerProps) => {
    const languageStrings = React.useContext(LanguageContext);
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchInputs, setSearchInputs] = React.useState<INameValuePairIndex[]>([]);
    const [boundedField, setBoundedField] = React.useState<string>("");
    const [boundName] = React.useState<string>(inputValue?.xml?.ContentTypeInputName ?? displayName);
    const dataSourceService = new DatasourceService();
    const dataSourceHelper = new DatasourceHelper(dataSourceService);
    const [dataSourceItem] = React.useState(
        dataSourceHelper.getInputDataSource(datasourceItems, boundName, [currentDirectory?.siteDisplayName ?? ""], [currentDirectory?.DocumentLibrary ?? ""])
    );
    const [selectedTags, setSelectedTags] = React.useState<ITag[]>([]); //selected tags
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        const setupAsyncWrapper = async () => {
            setIsLoading(true);
            try {
                const result = await dataSourceHelper.getSearchStructure(dataSourceItem);
                if (Array.isArray(result)) setSearchInputs(result.map((item, index) => ({ name: item, value: null, index: index })));
                const boundedFieldName = dataSourceHelper.getBoundedField(dataSourceItem, boundName);
                setBoundedField(boundedFieldName ?? "");
            } catch (error) {
                logger(error, "datasourceTagPicker.tsx (useEffect 1)");
            }
            setIsLoading(false);
        };
        setupAsyncWrapper();
    }, []);

    const isFirstRun = React.useRef(true);
    React.useEffect(() => {
        try {
            const newSelectedTag: ITag = inputValue?.value
                ? {
                    name: inputValue.value as string,
                    key: inputValue.value as string,
                }
                : null;

            setSelectedTags(newSelectedTag ? [newSelectedTag] : []);

            if (isFirstRun.current) {
                isFirstRun.current = false;
                return;
            }

            updateSearch(newSelectedTag?.key as string ?? "");
            updateInputValidationStatus(inputValidator.validate(inputValue?.value));
        } catch (e) {
            logger(e, "datasourceTagPicker.tsx (useEffect 2)");
        }
    }, [inputValue?.updatedFromDataSourceToggle, inputValue?.value]);

    const filterSuggestedTags = async (filterText: string, tagList: ITag[]): Promise<ITag[]> => {
        let suggestedTags: ITag[] = [];
        if (filterText?.trim()) {
            try {
                const request: IDatasourceQueryDto = {
                    id: dataSourceItem.id,
                    name: dataSourceItem.name,
                    tenantId: dataSourceItem.tenantId,
                    typeId: dataSourceItem.type.id,
                    typeName: dataSourceItem.type.name,
                    queryParams: [{ name: boundedField, value: filterText }],
                    returnOnlyQueriedProperties: true,
                    allowCache: dataSourceItem.allowCache
                };
                const result = await dataSourceService.Search(request);
                if (result.length > 0) {
                    const boundFieldProperty: string = Object.keys(result[0]).find(key => key.toLowerCase().trim() === boundedField.toLowerCase().trim());
                    if (boundFieldProperty) {
                        suggestedTags = result
                            .map<ITag>(resultRow => {
                                const propertyValue = resultRow[boundFieldProperty]?.trim();
                                return {
                                    name: propertyValue,
                                    key: propertyValue
                                }
                            })
                            .filter((tag, index, tags) => {
                                const tagName: string = tag.name.toLowerCase();
                                return tagName?.length > 0
                                    && tagName.indexOf(filterText.trim().toLowerCase()) !== -1
                                    && tags.findIndex(t => (t.name === tag.name)) === index
                                    && !listContainsTagList(tag, tagList)
                            });
                    }
                }
            } catch (error) {
                logger(error, "datasourceTagPicker.tsx (filterSuggestedTags)");
            }
        }
        return sortTerms(filterText, suggestedTags);
    };

    const closePanel = () => {
        setIsOpen(false);
    };

    const updateInputValue = (newValue: string) => {
        updateInputValidationStatus(inputValidator.validate([newValue]));
        updateSearch(newValue);
    };

    const updateSearchInputs = (index: number, value: string) => {
        dataSourceHelper.updateSearchInputs(index, value, searchInputs, setSearchInputs);
    };

    const openPanel = async () => {
        setIsOpen(true);
    };

    const updateSearch = (newValue: string) => {
        const index = searchInputs.findIndex(item => item.name?.trim().toLowerCase() === boundedField?.trim().toLowerCase());
        updateSearchInputs(index, newValue);
    };

    const onChangeHandle = (tags: ITag[]): void => {
        setSelectedTags(tags);
        // updateSearch(tags.map(tag => tag.key).join(', ') as string);
        updateSearch(tags?.length === 1 ? tags[0].key as string : "");
        updateInputValidationStatus(inputValidator.validate(tags.map(tag => tag.key).join(', ')));
    };

    const pickerSuggestionsProps: IBasePickerSuggestionsProps = {
        suggestionsHeaderText: languageStrings.Suggestions,
        noResultsFoundText: languageStrings.NoResultsFound,
    };

    const input = (
        <>
            <Label required={isRequired()}>{displayName}</Label>
            <TagPicker
                disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
                className="xb-sp-input"
                removeButtonAriaLabel="Remove"
                selectedItems={selectedTags}
                onResolveSuggestions={filterSuggestedTags}
                resolveDelay={300}
                getTextFromItem={getTextFromItem}
                pickerSuggestionsProps={pickerSuggestionsProps}
                onChange={onChangeHandle}
                inputProps={{
                    required: isRequired(),
                }}
            />
        </>
    );

    const stackTokens: IStackTokens = {
        childrenGap: 5
    };

    return (
        <>
            <Stack horizontal verticalAlign="start" tokens={stackTokens}>
                <Stack.Item grow={1}>{input}</Stack.Item>
                {inputValue?.xml?.ReadOnly !== SharePointMetadataBooleans.true && <Stack.Item>
                    {isLoading
                        ? <Spinner size={SpinnerSize.small} style={{ paddingTop: 36, marginLeft: -2 }} />
                        : <div className="xb-search-icon-container">
                            <TooltipHost content={`${languageStrings.Search} ${dataSourceItem?.name} ${languageStrings.Datasource}`}>
                                <FontIcon iconName="Search" onClick={openPanel} />
                            </TooltipHost>
                        </div>}
                </Stack.Item>}
            </Stack>
            <DataSourcePanel
                displayName={dataSourceItem?.name}
                closePanel={closePanel}
                isOpen={isOpen}
                datasourceItem={dataSourceItem}
                datasourceService={dataSourceService}
                searchInputs={searchInputs}
                updateSearchInputs={updateSearchInputs}
                updateInputValue={updateInputValue}
                boundedField={boundedField}
            />
        </>
    );
};

export const DatasourceTagPicker = connect(mapStateToProps, null)(UnconnectedDatasourceTagPicker);
